/**
 * Hourglass internal-staff access control.
 *
 * Gates the `/internal` workspace — a separate dashboard used by Hourglass
 * employees to inspect time entries on behalf of attorneys at the firm whose
 * tenant subdomain they're currently on.
 *
 * v1: any `@hourglass.law` email gets in. If we later need finer-grained
 * permissions (e.g., contractors with `@hourglass.law` who shouldn't see
 * editing), grow this into a `getHourglassPermissions(email)` shaped like
 * `getAdminPermissions` without breaking callers.
 */

export interface HourglassInternalOptions {
  /**
   * Whether the DEV override below applies. Defaults to true.
   *
   * Pass `false` for surfaces where an accidental DEV build would leak
   * something genuinely sensitive — the metrics dashboard aggregates every
   * attorney at a firm into a single ranked list, which is a different risk
   * profile from the per-attorney views the override exists to help build.
   */
  allowDevBypass?: boolean;
}

export const isHourglassInternal = (
  _userEmail?: string,
  options: HourglassInternalOptions = {},
): boolean => {
  if (!_userEmail) return false;

  const { allowDevBypass = true } = options;

  // Local visual-testing override: show the Internal workspace for any
  // signed-in user in dev without requiring an @hourglass.law address.
  if (allowDevBypass && import.meta.env.DEV) return true;

  return _userEmail.toLowerCase().endsWith("@hourglass.law");
};

/**
 * Strict variant with no DEV override — the email domain is the only way in.
 *
 * NOTE: this, like every check in this file, is a client-side UX gate only.
 * The endpoints behind the metrics dashboard must enforce the same rule
 * server-side; anyone can call the API directly.
 */
export const isHourglassInternalStrict = (userEmail?: string): boolean =>
  isHourglassInternal(userEmail, { allowDevBypass: false });
